/**
 * Tiny data layer around the API: one shared store per key (deduplicated requests, stale-while-revalidate),
 * refetch on matching agent events, on socket (re)open, when the tab comes back, and optional polling.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useSyncExternalStore } from "react";
import type { AgentEvent } from "../../shared/contracts";
import { ApiError, errorText, isAbortError } from "./api";
import { agentSocket, useAgentEvents, useSocketOpen } from "./socket";

export interface ResourceState<T> {
  data: T | undefined;
  error: ApiError | null;
  loading: boolean;
  /** Epoch of the last successful load (0 = never). */
  updatedAt: number;
}

type Fetcher<T> = (signal: AbortSignal) => Promise<T>;

class Resource<T> {
  state: ResourceState<T> = { data: undefined, error: null, loading: false, updatedAt: 0 };
  fetcher: Fetcher<T>;
  /** Epoch at which the last load was started. */
  startedAt = 0;
  private readonly listeners = new Set<() => void>();
  private inflight: Promise<void> | null = null;
  private dirty = false;
  private generation = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(fetcher: Fetcher<T>) {
    this.fetcher = fetcher;
  }

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  getSnapshot = (): ResourceState<T> => this.state;

  private set(patch: Partial<ResourceState<T>>): void {
    this.state = { ...this.state, ...patch };
    for (const fn of [...this.listeners]) fn();
  }

  /** Loads now; while a load is in flight, another one is queued right after it. */
  load(): Promise<void> {
    if (this.inflight) {
      this.dirty = true;
      return this.inflight;
    }
    this.inflight = this.run().finally(() => {
      this.inflight = null;
      if (this.dirty) {
        this.dirty = false;
        void this.load();
      }
    });
    return this.inflight;
  }

  /** Debounced load (coalesces bursts of events). */
  schedule(delayMs: number): void {
    if (this.timer) return;
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.load();
    }, delayMs);
  }

  mutate(update: (data: T | undefined) => T | undefined): void {
    if (this.inflight) this.dirty = true; // a response already on its way may predate this change
    this.set({ data: update(this.state.data) });
  }

  dispose(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.generation += 1;
  }

  private async run(): Promise<void> {
    const generation = ++this.generation;
    const controller = new AbortController();
    this.startedAt = Date.now();
    this.set({ loading: true });
    try {
      const data = await this.fetcher(controller.signal);
      if (generation !== this.generation) return;
      this.set({ data, error: null, loading: false, updatedAt: Date.now() });
    } catch (err) {
      if (generation !== this.generation || isAbortError(err)) return;
      this.set({ error: err instanceof ApiError ? err : new ApiError(0, "error", errorText(err)), loading: false });
    }
  }
}

const registry = new Map<string, Resource<unknown>>();

function resourceFor<T>(key: string, fetcher: Fetcher<T>): Resource<T> {
  let r = registry.get(key) as Resource<T> | undefined;
  if (!r) {
    r = new Resource<T>(fetcher);
    registry.set(key, r as Resource<unknown>);
  }
  return r;
}

/** Forgets every cached resource (logout). */
export function clearResources(): void {
  for (const r of registry.values()) r.dispose();
  registry.clear();
}

/** Reloads the resources whose key starts with `prefix` (after a mutation elsewhere). */
export function invalidate(prefix: string): void {
  for (const [key, r] of registry) if (key.startsWith(prefix)) r.schedule(0);
}

/** Updates a cached resource in place, if it exists. */
export function mutateResource<T>(key: string, update: (data: T | undefined) => T | undefined): void {
  const r = registry.get(key) as Resource<T> | undefined;
  r?.mutate(update);
}

export interface ResourceOptions<T> {
  /** Event types (or a predicate) that make this resource refetch. */
  refreshOn?: AgentEvent["type"][] | ((event: AgentEvent) => boolean);
  /** Polling interval while the tab is visible; a function of the current data, false/0 = no polling. */
  pollMs?: number | false | ((data: T | undefined) => number | false);
  /** Skip the initial fetch when the cached data is younger than this (ms). Default 0: always revalidate. */
  staleMs?: number;
  /** When false, the resource is idle (no fetch). */
  enabled?: boolean;
}

export interface ResourceResult<T> {
  data: T | undefined;
  error: ApiError | null;
  /** True only while nothing has been loaded yet. */
  loading: boolean;
  /** True while any request is in flight. */
  refreshing: boolean;
  reload: () => Promise<void>;
  mutate: (update: (data: T | undefined) => T | undefined) => void;
}

/**
 * Shared, cached API resource. `key` must identify the request (include its parameters).
 * Example: `useResource("run:" + id, (signal) => api("GET /api/runs/:id", { params: { id }, signal }))`.
 */
export function useResource<T>(key: string, fetcher: Fetcher<T>, options: ResourceOptions<T> = {}): ResourceResult<T> {
  const resource = resourceFor(key, fetcher);
  const enabled = options.enabled !== false;
  const optionsRef = useRef(options);
  useLayoutEffect(() => {
    resource.fetcher = fetcher;
    optionsRef.current = options;
  });

  const state = useSyncExternalStore(resource.subscribe, resource.getSnapshot, resource.getSnapshot);

  useEffect(() => {
    if (!enabled) return;
    const stale = optionsRef.current.staleMs ?? 0;
    if (!resource.state.updatedAt || Date.now() - resource.state.updatedAt >= stale) void resource.load();
  }, [resource, enabled]);

  useAgentEvents((event) => {
    if (!enabled) return;
    const rule = optionsRef.current.refreshOn;
    if (!rule) return;
    const match = typeof rule === "function" ? rule(event) : rule.includes(event.type);
    if (match) resource.schedule(250);
  });

  // Events emitted before the socket (re)opened were missed: revalidate what was fetched before that.
  useSocketOpen(() => {
    if (enabled && resource.startedAt < agentSocket.openedAt) resource.schedule(50);
  });

  useEffect(() => {
    if (!enabled) return;
    const onVisible = () => {
      if (document.visibilityState === "visible" && Date.now() - resource.state.updatedAt > 30_000) void resource.load();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [resource, enabled]);

  const poll = options.pollMs;
  const pollMs = typeof poll === "function" ? poll(state.data) : poll;
  useEffect(() => {
    if (!enabled || !pollMs) return;
    const t = setInterval(() => {
      if (document.visibilityState === "visible") void resource.load();
    }, pollMs);
    return () => clearInterval(t);
  }, [resource, enabled, pollMs]);

  const reload = useCallback(() => resource.load(), [resource]);
  const mutate = useCallback((update: (data: T | undefined) => T | undefined) => resource.mutate(update), [resource]);

  return {
    data: state.data,
    error: state.error,
    loading: state.data === undefined && (state.loading || (enabled && state.updatedAt === 0 && !state.error)),
    refreshing: state.loading,
    reload,
    mutate,
  };
}
